import { useState, useEffect, useCallback } from "react";
import SuperAdminLayout from "../components/SuperAdminLayout";
import api from "../services/api";

const CATEGORIES = ["all", "general", "support", "billing", "partnership", "other"];
const STATUSES   = ["all", "unread", "read", "replied"];

const CATEGORY_ICONS = {
  general: "💬", support: "🛠️", billing: "💳",
  partnership: "🤝", other: "📋",
};

const STATUS_STYLES = {
  unread:  "bg-red-500/10 text-red-400 border border-red-500/20",
  read:    "bg-blue-500/10 text-blue-400 border border-blue-500/20",
  replied: "bg-green-500/10 text-green-400 border border-green-500/20",
};

export default function SuperAdminMessagesPage() {
  const [messages, setMessages]     = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState("unread");
  const [category, setCategory]     = useState("all");
  const [search, setSearch]         = useState("");
  const [selected, setSelected]     = useState(null);
  const [notes, setNotes]           = useState("");
  const [notesLoading, setNotesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast]           = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/super-admin/messages/stats");
      setStats(res.data);
    } catch {}
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTab !== "all")  params.append("status",   activeTab);
      if (category !== "all")   params.append("category", category);
      if (search.trim())        params.append("search",   search);
      const res = await api.get(`/super-admin/messages?${params}`);
      setMessages(res.data?.data || res.data || []);
    } catch {
      showToast("Failed to load messages.", "error");
    } finally {
      setLoading(false);
    }
  }, [activeTab, category, search]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleSelect = async (msg) => {
    setSelected(msg);
    setNotes(msg.admin_notes || "");
    if (msg.status === "unread") {
      try {
        await api.patch(`/super-admin/messages/${msg.id}/read`);
        setMessages((prev) =>
          prev.map((m) => m.id === msg.id ? { ...m, status: "read" } : m)
        );
        setSelected((prev) => prev ? { ...prev, status: "read" } : prev);
        fetchStats();
      } catch {}
    }
  };

  const handleSaveNotes = async () => {
    if (!notes.trim() || !selected) return;
    setNotesLoading(true);
    try {
      await api.patch(`/super-admin/messages/${selected.id}/notes`, { notes });
      setMessages((prev) =>
        prev.map((m) => m.id === selected.id ? { ...m, status: "replied", admin_notes: notes } : m)
      );
      setSelected((prev) => prev ? { ...prev, status: "replied", admin_notes: notes } : prev);
      showToast("Notes saved. Message marked as replied.");
      fetchStats();
    } catch {
      showToast("Failed to save notes.", "error");
    } finally {
      setNotesLoading(false);
    }
  };

  const handleDelete = async (msg) => {
    if (!window.confirm(`Delete message from ${msg.name}?`)) return;
    setActionLoading(`delete-${msg.id}`);
    try {
      await api.delete(`/super-admin/messages/${msg.id}`);
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      if (selected?.id === msg.id) setSelected(null);
      showToast("Message deleted.");
      fetchStats();
    } catch {
      showToast("Failed to delete.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const openEmail = (email, subject) => {
    window.open(`mailto:${email}?subject=Re: ${subject}`, "_blank");
  };

  const openWhatsApp = (phone) => {
    const clean = phone?.replace(/^0/, "254");
    window.open(`https://wa.me/${clean}`, "_blank");
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-KE", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) : "—";

  return (
    <SuperAdminLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium ${
          toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
        }`}>
          {toast.type === "error" ? "❌" : "✅"} {toast.message}
        </div>
      )}

      <div className="flex flex-col h-full" style={{ fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── HEADER ── */}
        <div className="px-7 py-6 border-b border-slate-800">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
                Contact Messages
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                Manage all incoming inquiries from the website.
              </p>
            </div>
            {stats?.unread > 0 && (
              <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-semibold px-4 py-2 rounded-xl">
                <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
                {stats.unread} unread
              </div>
            )}
          </div>

          {/* Stats row */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              {[
                { label: "Total",   value: stats.total,   color: "text-slate-300", bg: "bg-slate-800" },
                { label: "Unread",  value: stats.unread,  color: "text-red-400",   bg: "bg-red-500/10 border border-red-500/20" },
                { label: "Read",    value: stats.read,    color: "text-blue-400",  bg: "bg-blue-500/10 border border-blue-500/20" },
                { label: "Replied", value: stats.replied, color: "text-green-400", bg: "bg-green-500/10 border border-green-500/20" },
              ].map((s) => (
                <div key={s.label} className={`${s.bg} rounded-xl px-4 py-3`}>
                  <p className={`text-2xl font-extrabold ${s.color}`} style={{ fontFamily: "'Sora', sans-serif" }}>
                    {s.value}
                  </p>
                  <p className="text-slate-400 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* Left panel — message list */}
          <div className="w-full md:w-96 shrink-0 border-r border-slate-800 flex flex-col overflow-hidden">

            {/* Filters */}
            <div className="p-4 border-b border-slate-800 space-y-3">
              {/* Search */}
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"
                  fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 15.803a7.5 7.5 0 0 0 10.607 0z" />
                </svg>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search messages..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Status tabs */}
              <div className="flex gap-1 bg-slate-800 rounded-xl p-1">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => setActiveTab(s)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                      activeTab === s ? "bg-orange-500 text-white" : "text-slate-400 hover:text-white"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>

              {/* Category filter */}
              <div className="flex gap-1.5 flex-wrap">
                {CATEGORIES.map((c) => (
                  <button key={c} onClick={() => setCategory(c)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold capitalize transition-all ${
                      category === c
                        ? "bg-slate-600 text-white"
                        : "text-slate-500 hover:text-slate-300"
                    }`}>
                    {c !== "all" && CATEGORY_ICONS[c]} {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Message list */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-800">
              {loading ? (
                <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Loading...
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-20 text-slate-500">
                  <p className="text-3xl mb-2">📭</p>
                  <p className="text-sm">No messages found.</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <button key={msg.id} onClick={() => handleSelect(msg)}
                    className={`w-full text-left px-4 py-4 hover:bg-slate-800/60 transition-colors ${
                      selected?.id === msg.id ? "bg-slate-800 border-l-2 border-orange-500" : ""
                    }`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-base shrink-0">{CATEGORY_ICONS[msg.category] || "📋"}</span>
                        <p className={`text-sm truncate ${
                          msg.status === "unread" ? "font-bold text-white" : "font-medium text-slate-300"
                        }`}>
                          {msg.name}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {msg.status === "unread" && (
                          <span className="w-2 h-2 bg-red-400 rounded-full" />
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[msg.status]}`}>
                          {msg.status}
                        </span>
                      </div>
                    </div>
                    <p className={`text-xs truncate mb-1 ${
                      msg.status === "unread" ? "text-slate-200" : "text-slate-400"
                    }`}>
                      {msg.subject}
                    </p>
                    <p className="text-slate-500 text-xs">
                      {formatDate(msg.created_at)}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right panel — message detail */}
          <div className="flex-1 overflow-y-auto hidden md:flex flex-col">
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <p className="text-5xl mb-4">📬</p>
                <p className="font-medium text-slate-400">Select a message to read</p>
                <p className="text-sm mt-1">Click any message on the left to view details.</p>
              </div>
            ) : (
              <div className="p-7 space-y-6">

                {/* Message header */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{CATEGORY_ICONS[selected.category] || "📋"}</span>
                      <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
                        {selected.subject}
                      </h2>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`text-xs px-2.5 py-1 rounded-full capitalize font-semibold ${STATUS_STYLES[selected.status]}`}>
                        {selected.status}
                      </span>
                      <span className="text-slate-400 text-xs capitalize">
                        {CATEGORY_ICONS[selected.category]} {selected.category}
                      </span>
                      <span className="text-slate-500 text-xs">{formatDate(selected.created_at)}</span>
                    </div>
                  </div>

                  {/* Delete button */}
                  <button
                    onClick={() => handleDelete(selected)}
                    disabled={actionLoading === `delete-${selected.id}`}
                    className="text-slate-500 hover:text-red-400 transition-colors p-2 rounded-xl hover:bg-red-500/10 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                </div>

                {/* Sender info */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-4">
                    Sender Details
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: "Name",  value: selected.name },
                      { label: "Email", value: selected.email },
                      { label: "Phone", value: selected.phone || "Not provided" },
                    ].map((row) => (
                      <div key={row.label}>
                        <p className="text-slate-500 text-xs">{row.label}</p>
                        <p className="text-slate-200 text-sm font-medium mt-0.5">{row.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Quick actions */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => openEmail(selected.email, selected.subject)}
                      className="flex items-center gap-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-400 text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
                    >
                      📧 Reply via Email
                    </button>
                    {selected.phone && (
                      <button
                        onClick={() => openWhatsApp(selected.phone)}
                        className="flex items-center gap-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
                      >
                        💬 WhatsApp
                      </button>
                    )}
                  </div>
                </div>

                {/* Message body */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-3">
                    Message
                  </p>
                  <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </p>
                </div>

                {/* Admin notes */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-3">
                    Internal Notes / Response Summary
                  </p>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about how this was handled, what was replied, etc..."
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none mb-3"
                  />
                  <button
                    onClick={handleSaveNotes}
                    disabled={!notes.trim() || notesLoading}
                    className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
                  >
                    {notesLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                        </svg>
                        Saving...
                      </>
                    ) : "Save Notes & Mark Replied"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
}